// Per-item dose totals and occurrence/streak/timing/trend statistics for the
// ItemReportModal Stats and Chart tabs (ITEM_REPORT_ENHANCEMENTS_PLAN.md
// D-IE-6/7/8/9 for dose totals, the rest of Task 1.2 for occurrence stats).
// Pure, deterministic, client-side only (D-IE-19) — no I/O, no re-filtering:
// callers pass already item-scoped, already-filtered events (see itemEvents()).

import type { MedicalEvent } from "./types.js";
import { MED_UNITS, type MedUnit } from "./medication.js";
import { localCalendarDate } from "./datetime.js";

const DAY_MS = 24 * 60 * 60 * 1000;

const isMedUnit = (u: unknown): u is MedUnit =>
  typeof u === "string" && (MED_UNITS as readonly string[]).includes(u);

/** `date` (YYYY-MM-DD) shifted by `offsetDays`, via the Date.UTC walk `chartDays`
 *  uses — never local Date construction, so DST never shifts a day boundary. */
function utcDayWalk(date: string, offsetDays: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + offsetDays);
  return dt.toISOString().slice(0, 10);
}

/** Whole calendar days from `a` to `b` (b - a), both YYYY-MM-DD. */
function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / DAY_MS);
}

/** `windowDays` local calendar dates ending at (and including) `endDate`, ascending. */
function windowDateList(endDate: string, windowDays: number): string[] {
  const out: string[] = [];
  for (let i = windowDays - 1; i >= 0; i--) out.push(utcDayWalk(endDate, -i));
  return out;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

// ── Dose totals (D-IE-7/8/9) ───────────────────────────────────────────────

/** Non-convertible dose "families". mcg/mg/g fold into "mass"; mL/tsp/tbsp fold
 *  into "volume"; every other MedUnit is its own family (D-IE-7). */
export type DoseUnitFamily =
  | "mass"
  | "volume"
  | "IU"
  | "unit"
  | "mEq"
  | "%"
  | "drop"
  | "spray"
  | "puff"
  | "tablet"
  | "capsule"
  | "patch";

/** Display unit for a family's totals (mass totals are reported in mg). */
export const FAMILY_DISPLAY_UNIT: Record<DoseUnitFamily, string> = {
  mass: "mg",
  volume: "mL",
  IU: "IU",
  unit: "unit",
  mEq: "mEq",
  "%": "%",
  drop: "drop",
  spray: "spray",
  puff: "puff",
  tablet: "tablet",
  capsule: "capsule",
  patch: "patch",
};

export function doseUnitFamily(unit: MedUnit): DoseUnitFamily {
  if (unit === "mg" || unit === "mcg" || unit === "g") return "mass";
  if (unit === "mL" || unit === "tsp" || unit === "tbsp") return "volume";
  return unit as DoseUnitFamily;
}

/** Convert one dose into its family's base unit (mg for mass, mL for volume,
 *  identity otherwise). 1 tsp = 5 mL, 1 tbsp = 15 mL. */
export function toBaseAmount(dose: number, unit: MedUnit): number {
  switch (unit) {
    case "mcg":
      return dose / 1000;
    case "mg":
      return dose;
    case "g":
      return dose * 1000;
    case "mL":
      return dose;
    case "tsp":
      return dose * 5;
    case "tbsp":
      return dose * 15;
    default:
      return dose;
  }
}

export interface DoseFamilySummary {
  family: DoseUnitFamily;
  displayUnit: string; // FAMILY_DISPLAY_UNIT[family]
  doseCount: number; // number of contributing doses (events) in the supplied events
}

export interface DoseTotalsResult {
  /** Families present, sorted by doseCount desc, then family name asc. */
  families: DoseFamilySummary[];
  /** family -> (yyyy-mm-dd -> summed base-unit amount). Only days with data. */
  byFamily: Map<DoseUnitFamily, Map<string, number>>;
  /** Events that contributed nothing (no numeric dose+unit, or no matching ingredient). */
  skippedEventCount: number;
  /** Distinct ingredient names seen across combination-product events, sorted
   *  by frequency desc (for the ingredient selector, D-IE-9). */
  ingredientNames: string[];
  /** True when at least one event carried a top-level numeric dose+unit. */
  hasTopLevelDoses: boolean;
}

/**
 * Sum per-day dose amounts for already-item-scoped medication events.
 * `ingredientName === undefined` means "the medication itself": only top-level
 * dose/unit contributes and combination-product events are skipped.
 * `ingredientName` set means: only the matching entry of `payload.ingredients[]`
 * contributes (case-insensitive match), and top-level doses are skipped.
 * Doses of different ingredients are NEVER summed (PLAN.md D-6 / D-IE-9).
 */
export function buildDoseTotals(
  events: MedicalEvent[],
  timezone: string,
  ingredientName?: string
): DoseTotalsResult {
  const byFamily = new Map<DoseUnitFamily, Map<string, number>>();
  const familyDoseCount = new Map<DoseUnitFamily, number>();
  const ingredientTally = new Map<string, { name: string; count: number }>();
  let skippedEventCount = 0;
  let hasTopLevelDoses = false;
  const wantIngredient = ingredientName?.trim().toLowerCase();

  for (const e of events) {
    if (e.eventType !== "medication_taken" && e.eventType !== "as_needed_med") continue;
    const p = e.payload as Record<string, unknown>;
    const ingredients = Array.isArray(p.ingredients)
      ? (p.ingredients as { name?: unknown; dose?: unknown; unit?: unknown }[])
      : undefined;
    const isCombination = !!ingredients && ingredients.length > 0;

    if (isCombination) {
      for (const ing of ingredients!) {
        if (typeof ing.name !== "string" || !ing.name.trim()) continue;
        const trimmed = ing.name.trim();
        const key = trimmed.toLowerCase();
        const existing = ingredientTally.get(key);
        if (existing) existing.count += 1;
        else ingredientTally.set(key, { name: trimmed, count: 1 });
      }
    } else if (typeof p.dose === "number" && p.dose > 0 && isMedUnit(p.unit)) {
      hasTopLevelDoses = true;
    }

    let dose: number | undefined;
    let unit: MedUnit | undefined;

    if (wantIngredient === undefined) {
      if (isCombination) {
        skippedEventCount += 1;
        continue;
      }
      if (typeof p.dose === "number" && p.dose > 0 && isMedUnit(p.unit)) {
        dose = p.dose;
        unit = p.unit;
      }
    } else {
      if (!isCombination) {
        skippedEventCount += 1;
        continue;
      }
      const match = ingredients!.find(
        (i) => typeof i.name === "string" && i.name.trim().toLowerCase() === wantIngredient
      );
      if (match && typeof match.dose === "number" && match.dose > 0 && isMedUnit(match.unit)) {
        dose = match.dose;
        unit = match.unit;
      }
    }

    if (dose === undefined || unit === undefined) {
      skippedEventCount += 1;
      continue;
    }

    const family = doseUnitFamily(unit);
    const day = localCalendarDate(e.occurredAt, timezone);
    const base = toBaseAmount(dose, unit);
    let dayMap = byFamily.get(family);
    if (!dayMap) {
      dayMap = new Map();
      byFamily.set(family, dayMap);
    }
    dayMap.set(day, Math.round(((dayMap.get(day) ?? 0) + base) * 10000) / 10000);
    familyDoseCount.set(family, (familyDoseCount.get(family) ?? 0) + 1);
  }

  const families: DoseFamilySummary[] = [...familyDoseCount.entries()]
    .map(([family, doseCount]) => ({ family, displayUnit: FAMILY_DISPLAY_UNIT[family], doseCount }))
    .sort((a, b) => b.doseCount - a.doseCount || a.family.localeCompare(b.family));

  const ingredientNames = [...ingredientTally.values()]
    .sort((a, b) => b.count - a.count)
    .map((e) => e.name);

  return { families, byFamily, skippedEventCount, ingredientNames, hasTopLevelDoses };
}

// ── Occurrence / streak / timing / trend stats ─────────────────────────────

export const MIN_STREAK_DAYS = 2; // D-IE-6

export type TimeBucket = "morning" | "afternoon" | "evening" | "night";
/** morning 05:00-11:59, afternoon 12:00-16:59, evening 17:00-21:59, night 22:00-04:59 (D-IE assumption 7). */
export const TIME_BUCKETS: readonly TimeBucket[] = ["morning", "afternoon", "evening", "night"];

function timeBucketFor(hour: number): TimeBucket {
  if (hour >= 5 && hour <= 11) return "morning";
  if (hour >= 12 && hour <= 16) return "afternoon";
  if (hour >= 17 && hour <= 21) return "evening";
  return "night";
}

export interface StreakStats {
  count: number; // runs of >= MIN_STREAK_DAYS consecutive logged days
  longest: number; // longest such run, in days (0 if none)
  avgLength: number; // mean length of counted runs, 1 decimal (0 if none)
  currentLength: number; // length of a run ending today; 0 if today has no entry
  avgGapDays: number | null; // mean length of zero-day runs strictly between counted runs; null if < 2 runs
  singleDayCount: number; // isolated logged days (runs of exactly 1 day)
}

export interface OccurrenceStats {
  windowDays: number;
  startDate: string; // yyyy-mm-dd, inclusive
  endDate: string; // yyyy-mm-dd, inclusive (today)
  totalOccurrences: number;
  daysLogged: number; // distinct local days with >= 1 occurrence
  pctDaysLogged: number; // Math.round(daysLogged / windowDays * 100)
  avgPerLoggedDay: number; // 1 decimal; 0 when daysLogged === 0
  avgIntervalDays: number | null; // windowDays / daysLogged, 1 decimal; null when daysLogged === 0
  daysSinceLast: number | null; // whole days from the latest occurrence in ALL supplied
  // events (not clipped to the window) to endDate; null if none ever
  longestZeroRunDays: number; // longest run of consecutive no-entry days inside the window
  maxInOneDay: number;
  peakDay: string | null; // date of maxInOneDay; on ties, the most recent
  streaks: StreakStats;
  timeOfDay: Record<TimeBucket, number>;
  mostCommonHour: number | null; // 0-23 local; on ties, the earliest hour
  dayOfWeek: number[]; // length 7, index 0 = Sunday, local
  prev: { totalOccurrences: number; daysLogged: number } | null; // equal-length window
  // immediately preceding startDate; null if it holds no events
}

interface DaySegment {
  logged: boolean;
  length: number;
  startIdx: number;
  endIdx: number;
}

function segmentDays(days: string[], dayCounts: Map<string, number>): DaySegment[] {
  const segs: DaySegment[] = [];
  let i = 0;
  while (i < days.length) {
    const logged = dayCounts.has(days[i]);
    let j = i;
    while (j < days.length && dayCounts.has(days[j]) === logged) j += 1;
    segs.push({ logged, length: j - i, startIdx: i, endIdx: j - 1 });
    i = j;
  }
  return segs;
}

function buildStreakStats(days: string[], dayCounts: Map<string, number>): StreakStats {
  const segs = segmentDays(days, dayCounts);
  const loggedRuns = segs.filter((s) => s.logged);
  const countedRuns = loggedRuns.filter((s) => s.length >= MIN_STREAK_DAYS);
  const singleDayCount = loggedRuns.filter((s) => s.length === 1).length;

  const longest = countedRuns.length > 0 ? Math.max(...countedRuns.map((s) => s.length)) : 0;
  const avgLength =
    countedRuns.length > 0
      ? round1(countedRuns.reduce((a, s) => a + s.length, 0) / countedRuns.length)
      : 0;

  let currentLength = 0;
  const lastSeg = segs[segs.length - 1];
  if (lastSeg && lastSeg.logged && lastSeg.endIdx === days.length - 1) {
    currentLength = lastSeg.length;
  }

  // avgGapDays: each maximal zero-run segment that lies strictly between two
  // counted runs is one entry in the average (D-IE-6); leading/trailing
  // zero-runs (outside the first/last counted run) are excluded.
  const countedSegIndices = segs
    .map((s, idx) => ({ s, idx }))
    .filter(({ s }) => s.logged && s.length >= MIN_STREAK_DAYS)
    .map(({ idx }) => idx);

  const gaps: number[] = [];
  for (let k = 0; k < countedSegIndices.length - 1; k++) {
    const from = countedSegIndices[k];
    const to = countedSegIndices[k + 1];
    for (let s = from + 1; s < to; s++) {
      if (!segs[s].logged) gaps.push(segs[s].length);
    }
  }
  const avgGapDays = gaps.length > 0 ? round1(gaps.reduce((a, b) => a + b, 0) / gaps.length) : null;

  return { count: countedRuns.length, longest, avgLength, currentLength, avgGapDays, singleDayCount };
}

/**
 * Occurrence statistics over the `windowDays` local calendar days ending today
 * (inclusive) — the identical window ItemReportModal's `chartDays` builds (D-IE-5).
 * `events` must already be item-scoped and filtered (see itemEvents()); it MAY
 * extend outside the window (needed for `daysSinceLast` and `prev`).
 */
export function buildOccurrenceStats(
  events: MedicalEvent[],
  timezone: string,
  windowDays: number,
  nowMs?: number
): OccurrenceStats {
  const now = nowMs ?? Date.now();
  const endDate = localCalendarDate(new Date(now).toISOString(), timezone);
  const days = windowDateList(endDate, windowDays);
  const startDate = days[0];

  const dayCounts = new Map<string, number>();
  for (const e of events) {
    const d = localCalendarDate(e.occurredAt, timezone);
    if (d < startDate || d > endDate) continue;
    dayCounts.set(d, (dayCounts.get(d) ?? 0) + 1);
  }

  const totalOccurrences = [...dayCounts.values()].reduce((a, b) => a + b, 0);
  const daysLogged = dayCounts.size;
  const pctDaysLogged = Math.round((daysLogged / windowDays) * 100);
  const avgPerLoggedDay = daysLogged > 0 ? round1(totalOccurrences / daysLogged) : 0;
  const avgIntervalDays = daysLogged > 0 ? round1(windowDays / daysLogged) : null;

  let latestDate: string | null = null;
  for (const e of events) {
    const d = localCalendarDate(e.occurredAt, timezone);
    if (latestDate === null || d > latestDate) latestDate = d;
  }
  const daysSinceLast = latestDate !== null ? daysBetween(latestDate, endDate) : null;

  let longestZeroRunDays = 0;
  let curZero = 0;
  for (const d of days) {
    if (dayCounts.has(d)) {
      curZero = 0;
    } else {
      curZero += 1;
      longestZeroRunDays = Math.max(longestZeroRunDays, curZero);
    }
  }

  let maxInOneDay = 0;
  let peakDay: string | null = null;
  for (const d of days) {
    const c = dayCounts.get(d) ?? 0;
    if (c > 0 && c >= maxInOneDay) {
      maxInOneDay = c;
      peakDay = d;
    }
  }

  const streaks = buildStreakStats(days, dayCounts);

  const timeOfDay: Record<TimeBucket, number> = { morning: 0, afternoon: 0, evening: 0, night: 0 };
  const hourCounts = new Array(24).fill(0);
  const dayOfWeek = new Array(7).fill(0);
  const DOW_INDEX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const hourFormatter = new Intl.DateTimeFormat("en-US", { timeZone: timezone, hour: "numeric", hour12: false });
  const dowFormatter = new Intl.DateTimeFormat("en-US", { timeZone: timezone, weekday: "short" });

  for (const e of events) {
    const d = localCalendarDate(e.occurredAt, timezone);
    if (d < startDate || d > endDate) continue;
    const instant = new Date(e.occurredAt);
    let hour = Number(hourFormatter.format(instant));
    if (hour === 24) hour = 0;
    hourCounts[hour] += 1;
    timeOfDay[timeBucketFor(hour)] += 1;
    const dowIdx = DOW_INDEX[dowFormatter.format(instant)];
    if (dowIdx !== undefined) dayOfWeek[dowIdx] += 1;
  }

  let mostCommonHour: number | null = null;
  let maxHourCount = 0;
  for (let h = 0; h < 24; h++) {
    if (hourCounts[h] > maxHourCount) {
      maxHourCount = hourCounts[h];
      mostCommonHour = h;
    }
  }

  const prevEndDate = utcDayWalk(startDate, -1);
  const prevDays = windowDateList(prevEndDate, windowDays);
  const prevStartDate = prevDays[0];
  let prevTotal = 0;
  const prevDayCounts = new Map<string, number>();
  for (const e of events) {
    const d = localCalendarDate(e.occurredAt, timezone);
    if (d < prevStartDate || d > prevEndDate) continue;
    prevTotal += 1;
    prevDayCounts.set(d, (prevDayCounts.get(d) ?? 0) + 1);
  }
  const prev = prevTotal > 0 ? { totalOccurrences: prevTotal, daysLogged: prevDayCounts.size } : null;

  return {
    windowDays,
    startDate,
    endDate,
    totalOccurrences,
    daysLogged,
    pctDaysLogged,
    avgPerLoggedDay,
    avgIntervalDays,
    daysSinceLast,
    longestZeroRunDays,
    maxInOneDay,
    peakDay,
    streaks,
    timeOfDay,
    mostCommonHour,
    dayOfWeek,
    prev,
  };
}
